import DataTypes from "@/utils/constants/DataTypes.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import MetaData from "@/utils/common/MetaData.ts";
import GenericActions from "@/utils/constants/GenericActions.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import dayjs from "@/utils/dayjs.ts";
import GenericValue = namaTypes.GenericValue;

export default class GenericValues {
  static createGenericValueBasedOnTheValue(fieldId: string, value: any, stringValue?: string): GenericValue {
    if (value === undefined || value === null)
      return {fieldId: fieldId, dataType: DataTypes.STRING};
    if (value === true || value === false)
      return {fieldId: fieldId, dataType: DataTypes.BOOLEAN, booleanData: value};
    if (typeof value === "string" && dayjs(value).isValid())
      return this.date(fieldId, value);
    if (typeof value === "object" && value.entityType)
      return this.createReferenceGenericValue(fieldId, value);
    return {fieldId: fieldId, dataType: DataTypes.STRING, textData: stringValue ?? value};
  }

  static createReferenceGenericValue(fieldId: string, value: any): Record<string, any> {
    return {
      dataType: DataTypes.ENTITYREFERENCE,
      fieldId: fieldId,
      entityReferenceData: value
    }
  }

  static getValue(genericValue: namaTypes.GenericValue): any {
    switch (genericValue.dataType) {
      case DataTypes.BIGDECIMAL:
        return genericValue.bigDecimalData;
      case DataTypes.STRING:
        return genericValue.textData;
      case DataTypes.BOOLEAN:
        return genericValue.booleanData;
      case DataTypes.INTEGER:
        return genericValue.integerData;
      case DataTypes.DATE:
        return genericValue.dateData;
      case DataTypes.LONG:
        return genericValue.longData;
      case DataTypes.ENTITYREFERENCE:
        return genericValue.entityReferenceData;
      case DataTypes.NORMALLIST:
      case DataTypes.FLATOBJECT:
      case DataTypes.FLATOBJECTLIST:
        return genericValue.realFlatObject;
      case DataTypes.ATTACHMENT:
        return genericValue.attachmentInfo;
    }
    return genericValue.jsonData;
  }

  static createGenericValue(fieldId: string, entityMetaData: namaTypes.NaMaMetaData, value: any, row?: number) {
    let fieldMetadata = MetaData.findFieldMetadata(fieldId, entityMetaData);
    if (!fieldMetadata || !fieldMetadata.fieldType)
      return this.createGenericValueBasedOnTheValue(fieldId, value);
    let fieldType = fieldMetadata.fieldType;
    if (fieldType === FieldType.Detail && ObjectChecker.isNotNullOrUndefined(row))
      return this.createGenericValueForObject(fieldId, value, entityMetaData.owner?.ownerId);
    return this.createGenericValueForType(fieldId, value, fieldType)
  }

  static createWholeEntityValue(fieldId: string, object: Record<string, any> | undefined, entityType: string): GenericValue {
    return {
      dataType: DataTypes.GUI2WholeEntity,
      fieldId: fieldId,
      gui2EntityType: entityType,
      jsonData: object ? JSON.stringify(object) : undefined,
    }
  }

  static createGenericValueForType(fieldId: string, value: any, fieldType?: string, parentDetail?) {
    if (!fieldType)
      return this.createGenericValueBasedOnTheValue(fieldId, value);
    switch (fieldType) {
      case FieldType.Decimal:
        return this.bigDecimal(fieldId, value);
      case FieldType.BigText:
      case FieldType.Enum:
      case FieldType.ID:
      case FieldType.EntityType:
      case FieldType.Link:
      case FieldType.Email:
      case FieldType.Measures:
      case FieldType.Password:
      case FieldType.FieldID:
      case FieldType.FeatureID:
      case FieldType.LatLng:
      case FieldType.Color:
      case FieldType.PhoneNumber:
      case FieldType.Text:
        return this.text(fieldId, value);
      case FieldType.Boolean:
        return this.boolean(fieldId, value);
      case FieldType.Integer:
        return this.integer(fieldId, value);
      case FieldType.Long:
        return this.long(fieldId, value);
      case FieldType.Time:
      case FieldType.DateAndTime:
      case FieldType.Date:
        return this.date(fieldId, value);
      case FieldType.Genericreference:
      case FieldType.Reference:
        return this.reference(fieldId, value);
      case FieldType.Detail:
        return this.list(fieldId, value, parentDetail);
      case FieldType.Binary:
        return this.attachment(fieldId, value);

    }
    return this.text(fieldId, value);
  }

  private static attachment(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.ATTACHMENT,
      fieldId: fieldId,
      attachmentInfo: val ? val.attachmentInfo : null
    };
  }

  private static bigDecimal(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.BIGDECIMAL,
      fieldId: fieldId,
      bigDecimalData: val
    };
  }

  private static text(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.STRING,
      fieldId: fieldId,
      textData: val
    };
  }

  private static boolean(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.BOOLEAN,
      fieldId: fieldId,
      booleanData: val
    };
  }

  private static integer(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.INTEGER,
      fieldId: fieldId,
      integerData: val
    };
  }

  private static date(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.DATE,
      fieldId: fieldId,
      dateData: val
    };
  }

  private static long(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.LONG,
      fieldId: fieldId,
      longData: val
    };
  }

  private static reference(fieldId: string, val: any): GenericValue {
    return {
      dataType: DataTypes.ENTITYREFERENCE,
      fieldId: fieldId,
      entityReferenceData: val
    };
  }

  static list(fieldId: string, val: any, parentDetail?): GenericValue {
    return {
      dataType: DataTypes.NORMALLIST,
      fieldId: fieldId,
      listData: {dataType: DataTypes.NORMALLIST, parantDetail: parentDetail, list: val}
    };
  }

  static createGenericValueForList(fieldId: string, value: any, gui2ListType?: string, parentDetail?: string): namaTypes.GenericValue {
    if (!Array.isArray(value))
      value = [value];
    return {
      dataType: DataTypes.GUI2List,
      fieldId: fieldId,
      jsonData: NamaCommon.stringify(value),
      gui2ClassName: gui2ListType,
      listData: {dataType: DataTypes.GUI2List, parantDetail: parentDetail}
    }
  }

  static createGenericValueForObject(fieldId: string, value: any, entityType: string | undefined, gui2ClassName?: string): namaTypes.GenericValue {
    if (!gui2ClassName && fieldId === GenericActions.INTERNAL_IMPORT_INTO_REFERENCE)
      gui2ClassName = "com.namasoft.common.flatobjects.EntityReferenceData";
    return {
      dataType: typeof value === "string" ? DataTypes.STRING : DataTypes.GUI2FlatObject,
      fieldId: fieldId,
      jsonData: value ? (typeof value === 'string') ? value : NamaCommon.stringify(value) : undefined,
      textData: typeof value === "string" ? value : undefined,
      gui2EntityType: entityType,
      gui2ClassName: gui2ClassName
    }
  }

  static sendAttachmentsAsLargeData(contextFieldIds: string[], contextFields: Record<string, GenericValue>, record: Record<string, any>, entityType: string) {
    for (let fieldId of contextFieldIds) {
      if (contextFields[fieldId]?.dataType !== DataTypes.ATTACHMENT)
        continue;
      contextFields[fieldId] = GenericValues.createGenericValueForObject(fieldId, NamaCommon.getFieldValue(record, fieldId),
        entityType, "com.namasoft.contracts.common.dtos.DTOLargeData");
    }
  }
}
